import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PhotoSlot } from "@/components/PhotoSlot";
import { GALLERY_SLOTS } from "@/data";

export const Gallery = () => {
  const { t, lang } = useLanguage();
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? GALLERY_SLOTS[activeIdx] : null;

  const prev = useCallback(() => setActiveIdx((i) => (i - 1 + GALLERY_SLOTS.length) % GALLERY_SLOTS.length), []);
  const next = useCallback(() => setActiveIdx((i) => (i + 1) % GALLERY_SLOTS.length), []);

  useEffect(() => {
    if (activeIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, prev, next]);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 lg:py-36 bg-coal/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead title={t.gallery.title} sub={t.gallery.sub} testId="gallery-title" />

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
          {GALLERY_SLOTS.map((slot, i) => (
            <Reveal key={slot.id} delay={(i % 3) * 0.08}>
              <button
                data-testid={`gallery-item-${slot.id}`}
                onClick={() => setActiveIdx(i)}
                className="w-full text-left focus:outline-none focus:ring-1 focus:ring-gold"
              >
                {slot.src ? (
                  <div data-testid={`gallery-photo-${slot.id}`} className="relative aspect-[4/3] overflow-hidden border border-gold/25 group bg-[#0e0e10]">
                    <img
                      src={slot.src}
                      alt={slot.label[lang] ?? slot.label.en}
                      loading="lazy"
                      className={`absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105 ${slot.fit === "contain" ? "object-contain" : "object-cover object-top"}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                ) : (
                  <PhotoSlot id={slot.id} label={slot.label[lang] ?? slot.label.en} ratio="aspect-[4/3]" testId={`gallery-photo-${slot.id}`} />
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] gc-glass flex items-center justify-center p-4 sm:p-10"
            onClick={() => setActiveIdx(null)}
          >
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 sm:left-8 z-10 p-3 border border-ivory/20 text-ivory hover:border-gold hover:text-gold transition-colors duration-300"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 sm:right-8 z-10 p-3 border border-ivory/20 text-ivory hover:border-gold hover:text-gold transition-colors duration-300"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <motion.div
              key={active.id}
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {active.src ? (
                <div data-testid="lightbox-photo" className="relative aspect-[4/3] overflow-hidden border border-gold/25 bg-card2">
                  <img src={active.src} alt={active.label[lang] ?? active.label.en} className="absolute inset-0 w-full h-full object-contain" />
                </div>
              ) : (
                <PhotoSlot id={active.id} label={active.label[lang] ?? active.label.en} ratio="aspect-[4/3]" testId="lightbox-photo" />
              )}
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="gc-eyebrow truncate">{active.label[lang] ?? active.label.en}</span>
                <div className="flex items-center gap-5 shrink-0">
                  <span className="text-[11px] tracking-[0.2em] text-dim">{activeIdx + 1} / {GALLERY_SLOTS.length}</span>
                  <button
                    data-testid="lightbox-close"
                    onClick={() => setActiveIdx(null)}
                    className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ash hover:text-gold transition-colors"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                    {t.gallery.close}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
